import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import React, { useState, useCallback } from 'react';
import classnames from 'clsx';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import CustomizableCalendarDay from 'react-dates/lib/components/CustomizableCalendarDay';

import { Button } from '../../button';

import '../../../styles/lib-overrides/react-dates.css';
// @ts-expect-error less import
import style from '../calendar-date-picker.module.css';

/**
 * @typedef {import('./calendar-date-picker-types.d.ts').CalendarDatePickerComponent} CalendarDatePickerComponent
 * @typedef {import('moment').Moment} Moment
 */

const NavPrev = <FontAwesomeIcon className={classnames(style['icon-wrapper'], style['left-arrow'])} icon={faArrowLeft} />;
const NavNext = <FontAwesomeIcon className={classnames(style['icon-wrapper'], style['right-arrow'])} icon={faArrowRight} />;

/**
 * @param {string} value
 * @param {string} dateFormat
 * @returns {Moment}
 */
const getMomentDate = (value, dateFormat) => {
  const momentSelectedDate = moment(value, dateFormat);
  return momentSelectedDate.isValid() ? momentSelectedDate : moment();
};

/**
 * @type {CalendarDatePickerComponent}
 */
export const CalendarDatePicker = ({
  allowPastDates = false,
  className = '',
  dateFormat = 'MM/DD/YYYY',
  isOutsideRange = null,
  keepOpenOnDateSelect = false,
  onChange,
  shouldAlwaysBeVisible = false,
  value,
  verticalSpacing = -20,
}) => {
  const [focused, setFocused] = useState(true);

  /**
   * @type {(day: Moment) => boolean}
   */
  const defaultIsOutsideRange = useCallback(
    (day) => {
      return allowPastDates ? false : moment().isAfter(day, 'day');
    },
    [allowPastDates],
  );

  const realIsOutsideRange = isOutsideRange || defaultIsOutsideRange;

  // Handle both string and string[] types for value
  const momentSelectedDate = !value ? null : getMomentDate(Array.isArray(value) ? value[0] : value, dateFormat || 'MM/DD/YYYY');

  /**
   * @type {(newDate: Moment) => void}
   */
  const change = useCallback(
    (newDate) => {
      if (!newDate) return;
      const formattedDate = newDate.format(dateFormat);
      onChange(formattedDate);
    },
    [dateFormat, onChange],
  );

  const onFocusChange = useCallback(() => {
    if (shouldAlwaysBeVisible) {
      return;
    }

    setFocused(false);
  }, [shouldAlwaysBeVisible]);

  return (
    <div className={classnames(className, style['calendar-date-picker'], 'components-date-picker')}>
      {/* @ts-expect-error - SingleDatePicker can indeed be used as a JSX component. Just no refs. */}
      <SingleDatePicker
        date={momentSelectedDate}
        daySize={30}
        displayFormat={dateFormat}
        focused={focused}
        hideKeyboardShortcutsPanel
        initialVisibleMonth={() => {
          return !!momentSelectedDate && momentSelectedDate.isValid() ? momentSelectedDate : moment();
        }}
        isOutsideRange={realIsOutsideRange}
        keepOpenOnDateSelect={keepOpenOnDateSelect}
        // @ts-expect-error - ReactNode type issue
        navNext={NavNext}
        // @ts-expect-error - ReactNode type issue
        navPrev={NavPrev}
        numberOfMonths={1}
        onDateChange={change}
        onFocusChange={onFocusChange}
        // @ts-expect-error - ReactNode type issues
        renderCalendarDay={({ onDayClick, day, daySize, key, tabIndex }) => {
          const dayStyle = {
            width: daySize,
            height: daySize,
          };
          if (!day) {
            return (
              // @ts-expect-error - CustomizableCalendarDay component type does not have a refs prop
              <CustomizableCalendarDay
                key={key}
                day={day}
                daySize={daySize}
                renderDayContents={() => <div className={style['day-placeholder']} style={dayStyle} />}
                tabIndex={tabIndex}
              />
            );
          }
          const selected = momentSelectedDate && day.format(dateFormat) === momentSelectedDate.format(dateFormat);
          const disabled = realIsOutsideRange(day);

          return (
            // @ts-expect-error - CustomizableCalendarDay component type does not have a refs prop
            <CustomizableCalendarDay
              key={key}
              day={day}
              daySize={daySize}
              defaultStyles={{}}
              hoveredSpanStyles={{}}
              onDayClick={onDayClick}
              renderDayContents={() => (
                <Button
                  key={key}
                  className={classnames(style['day-button'], {
                    [style.selected]: selected,
                  })}
                  disabled={disabled}
                  onClick={() => {
                    // @ts-expect-error - onDayClick expects an event as the 2nd argument but we've never passed one
                    onDayClick(day);
                  }}
                  style={dayStyle}
                  theme='unstyled'
                >
                  {day.date()}
                </Button>
              )}
              tabIndex={tabIndex}
            />
          );
        }}
        verticalSpacing={Math.max(verticalSpacing || -20, 1)}
      />
    </div>
  );
};
